import React from "react";
import { PurpleSwitch } from "../../../helpers/helpers";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import {
  API_get_live_alert_config,
  API_post_live_alert_config,
  API_get_company_live_alert_config,
  API_post_company_live_alert_config,
} from "../../../constants";
import { getRightworkAPI } from "../requests/utilities/requests";
import { postRightworkAPI } from "../requests/utilities/requests";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { createConfigAutoSaveQueue } from "./configAutoSaveQueue";

const controlSx = {
  borderRadius: "8px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
  height: "36px",
  backgroundColor: "white",
  fontSize: "0.75rem",
};

// Every editable field, so the save posts a complete payload.
const BOOL_FIELDS = [
  "alert_no_show_enabled",
  "alert_early_clock_in_enabled",
  "alert_late_clock_out_enabled",
  "alert_unscheduled_clock_in_enabled",
  "alert_break_overrun_enabled",
  "alert_break_reminder_enabled",
  "alert_no_break_long_shift_enabled",
  "alert_approaching_weekly_ot_enabled",
  "alert_minor_daily_hours_enabled",
  "alert_labor_over_forecast_enabled",
  "alert_sales_off_forecast_enabled",
];
const NUMBER_FIELDS = [
  "no_show_minutes",
  "early_clock_in_minutes",
  "late_clock_out_minutes",
  "break_overrun_minutes",
  "break_reminder_minutes",
  "no_break_shift_hours",
  "weekly_ot_warning_hours",
  "minor_daily_hours",
  "labor_over_forecast_pct",
  "sales_off_forecast_pct",
];

const minsAfterOpts = (arr) =>
  arr.map((m) => ({ value: m, label: m === 0 ? "At shift start" : `${m} min after` }));
const minsOverOpts = (arr) => arr.map((m) => ({ value: m, label: `${m} min over` }));
const minsBeforeOpts = (arr) =>
  arr.map((m) => ({ value: m, label: m === 0 ? "At break time" : `${m} min before` }));
const warnHrsOpts = (arr) => arr.map((h) => ({ value: h, label: `Warn at ${h} hrs` }));
const pctOpts = (arr, sign) => arr.map((p) => ({ value: p, label: `${sign}${p}%` }));

// Full payload, so auto-save can't leave a partially-written config.
const buildLiveAlertPayloadFor = (storeId, data) => {
  const requestData = { store_id: storeId };
  BOOL_FIELDS.forEach((f) => { requestData[f] = data[f]; });
  NUMBER_FIELDS.forEach((f) => { requestData[f] = data[f]; });
  return requestData;
};

const postLiveAlertConfig = async (storeId, data) => {
  const res = await postRightworkAPI(
    API_post_live_alert_config,
    buildLiveAlertPayloadFor(storeId, data)
  );
  if (res.status !== 200) throw new Error("Save failed");
  return res;
};

// Same payload against the company template. Every LiveAlertConfig field is
// defaultable: this panel has no labor-law governed rows.
const buildCompanyLiveAlertPayloadFor = (companyId, data) => {
  const requestData = { company_id: companyId };
  BOOL_FIELDS.forEach((f) => { requestData[f] = data[f]; });
  NUMBER_FIELDS.forEach((f) => { requestData[f] = data[f]; });
  return requestData;
};

const postCompanyLiveAlertConfig = async (companyId, data) => {
  const res = await postRightworkAPI(
    API_post_company_live_alert_config,
    buildCompanyLiveAlertPayloadFor(companyId, data)
  );
  if (res.status !== 200) throw new Error("Save failed");
  return res;
};

const liveAlertSaveErrorMessage = (error) =>
  error?.response?.data?.system?.message ||
  "Couldn't save Live Action Alerts. Please try again.";

// Lives outside React so it survives this panel unmounting mid-save (inline
// panels unmount on collapse). See configAutoSaveQueue.js.
const liveAlertQueue = createConfigAutoSaveQueue({
  // The panel was collapsed/switched before this save failed, so no mounted
  // component can toast or resync. Report it here instead of failing silently;
  // the queue cleared its cache, so the next mount refetches server state.
  onOrphanedError: (error) => {
    toast.error(liveAlertSaveErrorMessage(error), { autoClose: 4000 });
    console.error("Live alert auto-save failed after unmount:", error);
  },
});

export default function LiveAlertSettings(props) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [data, setData] = React.useState({});
  const [originalJson, setOriginalJson] = React.useState("");
  // Read-only: the labor-law violation categories this store's jurisdiction
  // flags live automatically (derived server-side from regulations).
  const [laborLawCategories, setLaborLawCategories] = React.useState([]);
  // autoSave mode: each change writes immediately (like the module toggles).
  // savingField holds the id of the in-flight change so its control can show a
  // spinner, mirroring the per-control spinner on the flag toggles.
  const [savingField, setSavingField] = React.useState(null);
  const isFieldSaving = (field) =>
    savingField != null && field != null && savingField === field;
  const savingSpinner = (field) =>
    props.autoSave && isFieldSaving(field) ? (
      <CircularProgress size={14} sx={{ color: "#64748b" }} />
    ) : null;

  // autoSave writes are serialized per config row by `liveAlertQueue`, which lives
  // outside React so it survives this panel unmounting mid-save (inline panels
  // unmount on collapse). See configAutoSaveQueue.js.
  //
  // `companyScope` edits the company's TEMPLATE rather than a store's config, so
  // it gets its own queue key and its own endpoints. The key namespace keeps a
  // company id from ever colliding with a store id.
  const queueKey = props.companyScope ? `company:${props.companyId}` : props.homeStore;
  const postForScope = props.companyScope
    ? (_key, data) => postCompanyLiveAlertConfig(props.companyId, data)
    : postLiveAlertConfig;

  const hasUnsavedChanges =
    originalJson !== "" && JSON.stringify(data) !== originalJson;

  // Tracks the config row currently being edited so any in-flight fetch can bail
  // if the Location selector has since moved. This covers both the store-switch
  // effect AND the post-save refresh (which runs from the saving render's
  // closure, so its key is the row that was saved, not the one now selected).
  // Otherwise a late response repopulates the form with the prior store's config
  // and a subsequent save copies it into the wrong store.
  const homeStoreRef = React.useRef(queueKey);

  const fetchConfig = async (showLoading) => {
    const requestedStore = queueKey;
    setIsLoading(showLoading);
    // Capture queue state BEFORE issuing the GET. A response read from the
    // server before an in-flight POST committed can still resolve after the
    // queue drains — judging staleness at resolve time would then adopt (and
    // cache) that stale snapshot, letting the next auto-save revert the edit.
    const startedBusy = liveAlertQueue.isBusy(requestedStore);
    const startVersion = liveAlertQueue.getVersion(requestedStore);
    try {
      const res = await getRightworkAPI(
        props.companyScope
          ? API_get_company_live_alert_config
          : API_get_live_alert_config,
        props.companyScope
          ? { company_id: props.companyId }
          : { store_id: props.homeStore }
      );
      if (homeStoreRef.current !== requestedStore) return;
      if (res.status === 200) {
        const cfg = res.data?.redux?.payload?.live_alert_config || {};
        // Stale if a save was in flight when the GET started, is in flight now,
        // or a commit landed during the fetch window. Then the cache (not the
        // server snapshot) is authoritative, and we must NOT overwrite it.
        const cached = liveAlertQueue.getLatest(requestedStore);
        const useCached = Boolean(
          cached && liveAlertQueue.isFetchStale(requestedStore, startedBusy, startVersion)
        );
        const effective = useCached ? cached : cfg;

        setData(effective);
        if (!useCached) {
          liveAlertQueue.setLatest(requestedStore, cfg);
        }
        setOriginalJson(JSON.stringify(effective));
        setLaborLawCategories(res.data?.redux?.payload?.labor_law_categories || []);
        setIsLoading(false);
      }
    } catch (error) {
      if (homeStoreRef.current !== requestedStore) return;
      throw Error(error);
    }
  };

  React.useEffect(() => {
    homeStoreRef.current = queueKey;
    setData({});
    fetchConfig(true);
  }, [queueKey]);

  // Follow the shared queue while mounted: clear the spinner when it drains, and
  // resync/report if a save failed. Unsubscribing on unmount lets the queue keep
  // draining without touching this component's state.
  React.useEffect(() => {
    if (!props.autoSave) return undefined;
    const unsubscribe = liveAlertQueue.subscribe(queueKey, {
      onBusyChange: (busy) => {
        if (!busy) setSavingField(null);
        // Lets an owner block actions that read this config from the server
        // (e.g. pushing a company default) until the write has landed.
        props.onSavingChange?.(busy);
      },
      onError: (error) => {
        setSavingField(null);
        fetchConfig(false);
        toast.error(liveAlertSaveErrorMessage(error), { autoClose: 3000 });
        console.error("Error:", error);
      },
    });
    return () => {
      unsubscribe();
      props.onSavingChange?.(false);
    };
  }, [queueKey, props.autoSave]);

  // All field changes route through here. In autoSave mode the patch is merged
  // into the queue's cached snapshot (the source of truth for the payload) and a
  // serialized save is scheduled; the merged snapshot renders optimistically.
  // In non-autoSave mode it just updates state and the Save button persists.
  const commitChange = (patch) => {
    if (props.autoSave) {
      setSavingField(Object.keys(patch)[0] ?? "__all__");
      // `data` is the baseline if the queue's cache was cleared by a failed
      // save, so the posted payload is never partial.
      const next = liveAlertQueue.commit(
        queueKey,
        patch,
        postForScope,
        data
      );
      setData(next);
      return;
    }
    setData((prev) => ({ ...prev, ...patch }));
  };

  const setField = (field) => (event) => {
    commitChange({ [field]: event.target.checked });
  };
  const setThreshold = (field) => (event) => {
    commitChange({ [field]: event.target.value });
  };

  const post = async () => {
    const requestData = buildLiveAlertPayloadFor(props.homeStore, data);
    try {
      toast.loading("Saving Live Action Alerts...", { autoClose: false });
      const res = await postRightworkAPI(API_post_live_alert_config, requestData);
      if (res.status === 200) {
        toast.dismiss();
        toast.success("Live Action Alerts saved", { autoClose: 3000 });
        fetchConfig(false);
      }
    } catch (error) {
      toast.dismiss();
      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }
      console.error("Error:", error);
    }
  };

  // ---- presentation helpers (plain functions, not components) ----
  const renderSelect = (value, onChange, options, width = 172, field) => (
    <div className="flex items-center gap-2">
      {savingSpinner(field)}
      <div style={{ width }}>
        <Select fullWidth value={value ?? ""} onChange={onChange} size="small" sx={controlSx}>
          {options.map((o) => (
            <MenuItem key={String(o.value)} value={o.value} sx={{ fontSize: "0.75rem" }}>{o.label}</MenuItem>
          ))}
        </Select>
      </div>
    </div>
  );

  // `managed` grays the row and disables its controls — used for compliance
  // alerts (overtime / minor / break) when labor law governs them via
  // jurisdiction regulations instead of this config.
  const renderRow = ({ label, checked, onChange, trailing, managed, field }) => (
    <div className={`flex items-center justify-between gap-4 py-3 ${managed ? "opacity-60" : ""}`}>
      <div className="flex items-center gap-1.5 min-w-0">
        <p className={`text-sm text-slate-800 ${props.inline ? "font-medium" : ""}`}>{label}</p>
        {managed && (
          <span className="ml-1 inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 whitespace-nowrap">
            Set by labor law
          </span>
        )}
      </div>
      <div
        className="flex items-center gap-2.5 shrink-0"
        style={managed ? { pointerEvents: "none" } : undefined}
      >
        {savingSpinner(field)}
        {trailing}
        <PurpleSwitch
          checked={Boolean(checked)}
          onChange={onChange}
          disabled={managed}
        />
      </div>
    </div>
  );

  // Inline (Manage Locations) matches the flatter module-card styling used by
  // the flag settings; non-inline keeps the larger headers.
  const renderCard = (title, description, children) => (
    <div
      className={
        props.inline
          ? "rounded-lg border border-slate-200 bg-white mb-3"
          : "rounded-xl border border-slate-200 bg-white shadow-sm mb-5"
      }
    >
      <div
        className={`border-b border-slate-100 ${props.inline ? "px-3 py-2.5" : "px-5 py-4"}`}
      >
        <h2
          className={
            props.inline
              ? "text-sm font-medium text-slate-900"
              : "text-lg font-semibold text-slate-900"
          }
        >
          {title}
        </h2>
        {description && (
          <p
            className={
              props.inline
                ? "mt-0.5 text-xs leading-relaxed text-slate-500"
                : "text-sm text-slate-500 mt-0.5"
            }
          >
            {description}
          </p>
        )}
      </div>
      <div
        className={`divide-y divide-slate-100 ${props.inline ? "px-3" : "px-5"}`}
      >
        {children}
      </div>
    </div>
  );

  return (
    <div>
      {!isLoading ? (
        <div className={props.inline ? "px-1 py-2" : "px-6 pt-6 pb-16 max-w-4xl"}>
          {/* Inline: the accordion row already labels + describes this surface,
              so the panel's own header (intro + Save button) is redundant. */}
          {!props.inline && (
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <p className="text-sm text-slate-500">
                  What alerts a manager on the floor while a shift is happening, so they
                  can act before it becomes a problem.
                </p>
              </div>
              {!props.autoSave && (
                <button
                  type="button"
                  disabled={!hasUnsavedChanges}
                  onClick={() => hasUnsavedChanges && post()}
                  className={`shrink-0 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    hasUnsavedChanges
                      ? "bg-violet-700 hover:bg-violet-800 text-white"
                      : "bg-slate-100 text-slate-400 cursor-not-allowed"
                  }`}
                >
                  Save changes
                </button>
              )}
            </div>
          )}

          {renderCard(
            "Arrivals & departures",
            "Real-time attendance against the published schedule.",
            <>
              {renderRow({
                label: "No-show / hasn't clocked in",
                checked: data.alert_no_show_enabled,
                onChange: setField("alert_no_show_enabled"),
                field: "alert_no_show_enabled",
                trailing: renderSelect(data.no_show_minutes, setThreshold("no_show_minutes"), minsAfterOpts([0, 5, 10, 15, 30]), 172, "no_show_minutes"),
              })}
              {renderRow({
                label: "Clocked in early",
                checked: data.alert_early_clock_in_enabled,
                onChange: setField("alert_early_clock_in_enabled"),
                field: "alert_early_clock_in_enabled",
                trailing: renderSelect(data.early_clock_in_minutes, setThreshold("early_clock_in_minutes"), [10, 15, 20, 30].map((m) => ({ value: m, label: `${m} min before` })), 172, "early_clock_in_minutes"),
              })}
              {renderRow({
                label: "Hasn't clocked out",
                checked: data.alert_late_clock_out_enabled,
                onChange: setField("alert_late_clock_out_enabled"),
                field: "alert_late_clock_out_enabled",
                trailing: renderSelect(data.late_clock_out_minutes, setThreshold("late_clock_out_minutes"), minsAfterOpts([15, 30, 45, 60]), 172, "late_clock_out_minutes"),
              })}
              {renderRow({
                label: "Clocked in without a shift",
                checked: data.alert_unscheduled_clock_in_enabled,
                onChange: setField("alert_unscheduled_clock_in_enabled"),
                field: "alert_unscheduled_clock_in_enabled",
              })}
            </>,
          )}

          {renderCard(
            "Breaks",
            "Break timing while a shift is in progress.",
            <>
              {renderRow({
                label: "Break running long",
                checked: data.alert_break_overrun_enabled,
                onChange: setField("alert_break_overrun_enabled"),
                field: "alert_break_overrun_enabled",
                trailing: renderSelect(data.break_overrun_minutes, setThreshold("break_overrun_minutes"), minsOverOpts([10, 15, 20, 30]), 172, "break_overrun_minutes"),
              })}
              {renderRow({
                label: "Break due soon (reminder)",
                checked: data.alert_break_reminder_enabled,
                onChange: setField("alert_break_reminder_enabled"),
                field: "alert_break_reminder_enabled",
                trailing: renderSelect(data.break_reminder_minutes, setThreshold("break_reminder_minutes"), minsBeforeOpts([0, 5, 10, 15]), 172, "break_reminder_minutes"),
              })}
              {renderRow({
                label: "Working long with no break yet",
                checked: data.alert_no_break_long_shift_enabled,
                onChange: setField("alert_no_break_long_shift_enabled"),
                field: "alert_no_break_long_shift_enabled",
                trailing: renderSelect(data.no_break_shift_hours, setThreshold("no_break_shift_hours"), warnHrsOpts([4, 5, 6, 7]), 172, "no_break_shift_hours"),
              })}
            </>,
          )}

          {renderCard(
            "Overtime & minors",
            "Catch thresholds before they're crossed, so labor can be redirected.",
            <>
              {renderRow({
                label: "Approaching weekly overtime",
                checked: data.alert_approaching_weekly_ot_enabled,
                onChange: setField("alert_approaching_weekly_ot_enabled"),
                field: "alert_approaching_weekly_ot_enabled",
                trailing: renderSelect(data.weekly_ot_warning_hours, setThreshold("weekly_ot_warning_hours"), warnHrsOpts([30, 35, 38, 40]), 172, "weekly_ot_warning_hours"),
              })}
              {renderRow({
                label: "Minor over daily hours",
                checked: data.alert_minor_daily_hours_enabled,
                onChange: setField("alert_minor_daily_hours_enabled"),
                field: "alert_minor_daily_hours_enabled",
                trailing: renderSelect(data.minor_daily_hours, setThreshold("minor_daily_hours"), warnHrsOpts([4, 5, 6, 7.5]), 172, "minor_daily_hours"),
              })}
            </>,
          )}

          {renderCard(
            "Labor targets",
            "Live pace against forecast. Operational, not compliance.",
            <>
              {renderRow({
                label: "Labor running over forecast",
                checked: data.alert_labor_over_forecast_enabled,
                onChange: setField("alert_labor_over_forecast_enabled"),
                field: "alert_labor_over_forecast_enabled",
                trailing: renderSelect(data.labor_over_forecast_pct, setThreshold("labor_over_forecast_pct"), pctOpts([10, 15, 20, 25], "+"), 172, "labor_over_forecast_pct"),
              })}
              {renderRow({
                label: "Sales pace off forecast",
                checked: data.alert_sales_off_forecast_enabled,
                onChange: setField("alert_sales_off_forecast_enabled"),
                field: "alert_sales_off_forecast_enabled",
                trailing: renderSelect(data.sales_off_forecast_pct, setThreshold("sales_off_forecast_pct"), pctOpts([10, 15, 20, 25], "−"), 172, "sales_off_forecast_pct"),
              })}
            </>,
          )}

          {laborLawCategories.length > 0 &&
            renderCard(
              "Labor law violations",
              "Flagged live automatically from this store's jurisdiction regulations. Not configurable here.",
              <>
                {laborLawCategories.map((cat) => (
                  <div
                    key={cat.type}
                    className="flex items-center justify-between gap-4 py-3 opacity-60"
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="text-sm text-slate-800">{cat.name}</p>
                      <span className="ml-1 inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 whitespace-nowrap">
                        Set by labor law
                      </span>
                    </div>
                    <span className="text-xs font-medium text-emerald-600 shrink-0">
                      Automatic
                    </span>
                  </div>
                ))}
              </>,
            )}
        </div>
      ) : (
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadSpinner />
        </div>
      )}
    </div>
  );
}
